import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ExternalLink, MessageCircle, Mail, Copy, ChevronDown } from 'lucide-react';
import API_BASE from '../../utils/apiBase';
import { getTrainingCourseOptions } from '../../lib/trainingCourseCatalogue';
import { suggestManagerRenewalLinks, TRAINING_RENEWAL_POLICY } from './trainingRenewalPlanner';

// Same source of course truth for every role's training need. Nothing here
// awards a qualification or bypasses a manager's evidence review.
const TOPICS = [
  ['safeguarding', 'Safeguarding Adults'],
  ['safeguarding_children', 'Safeguarding Children'],
  ['manual_handling', 'Moving & handling'],
  ['infection_control', 'Infection control'],
  ['basic_life_support', 'Basic life support'],
  ['fire_safety', 'Fire safety'],
  ['health_safety', 'Health & safety'],
  ['equality_diversity_human_rights', 'Equality, diversity & human rights'],
  ['information_governance', 'Information governance'],
  ['prevent', 'Prevent'],
  ['learning_disability_autism_tier_2', 'Learning disability & autism — Tier 2'],
  ['care_certificate', 'Care Certificate — new starter'],
];

// Fail closed: free courses are shareable only after provider-documented
// course-level accreditation is checked. Even accredited partial courses are
// not recommended as complete mandatory training.
export function allowedTrainingRecommendation(link) {
  if (!link?.url || link.supplementary || link.type === 'reference') return false;
  if (link.price !== 'Free') return true;
  const assurance = link.qualityAssurance;
  return assurance?.verified === true
    && assurance.cpdAccredited === true
    && assurance.cityAndGuildsAssured === true
    && assurance.scope === 'online_theory_only'
    && assurance.source === link.url
    && /^https:\/\/www\.healthandsafetygroup\.com\/healthcare\/elearning\/[a-z0-9-]+$/.test(link.url);
}

export function managerTrainingResources() {
  return TOPICS.flatMap(([key, topic]) => {
    const option = getTrainingCourseOptions({ code: key });
    const links = [
      option.paid && { ...option.paid, type: option.paid.price === 'Free' ? 'free' : 'provider' },
      option.free && { ...option.free, type: 'free' },
      option.practical && { ...option.practical, type: 'practical' },
      option.standards && { ...option.standards, type: 'reference' },
      ...(option.additional || []).map(item => ({ ...item, type: item.price === 'Free' ? 'free' : 'provider' })),
    ].filter(Boolean).filter(allowedTrainingRecommendation);
    return links.map((link, index) => ({
      id: `${key}:${index}`, key, topic, ...link,
      note: option.note || '',
      isSupplementary: Boolean(link.supplementary),
    }));
  });
}

export function buildTrainingShareMessage(name, selected) {
  if (!selected.length) return '';
  const greeting = name ? `Hi ${name},` : 'Hello,';
  return [
    greeting,
    'Your care training team has selected these course resources for you:',
    '',
    ...selected.flatMap((item, index) => [
      `${index + 1}. ${item.topic} — ${item.title}`,
      `${item.price === 'Free' ? 'Free · CPD accredited, City & Guilds assured (theory)' : 'Provider/booking'} · ${item.provider || 'Course provider'}`,
      item.url,
      ...(item.note ? [`Important: ${item.note}`] : []),
      '',
    ]),
    'Please follow the instructions from your training team. Afterwards, sign in to CareDock and upload your certificate(s).',
    'A course link is not proof of completion. Your organisation checks certificates and any required practical competence.',
  ].join('\n');
}

export default function ManagerTrainingResources({ employeeId, token, hub = {} }) {
  const resources = useMemo(() => managerTrainingResources(), []);
  const renewalPlan = useMemo(() => suggestManagerRenewalLinks(hub, resources), [hub, resources]);
  const [open, setOpen] = useState(false);
  const [contact, setContact] = useState(null);
  const [contactError, setContactError] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !employeeId || !token) return undefined;
    let active = true;
    setContactError('');
    axios.get(`${API_BASE}/employees/${employeeId}/training/hub/share-contact`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(response => {
      if (active) setContact(response.data);
    }).catch(() => { if (active) setContactError('Contact unavailable. You can still copy the links.'); });
    return () => { active = false; };
  }, [open, employeeId, token]);

  const chosen = resources.filter(item => selectedIds.includes(item.id));
  const message = buildTrainingShareMessage(contact?.first_name, chosen);
  const whatsAppNumber = String(contact?.whatsapp_recipient || '').replace(/[^0-9]/g, '');
  const email = String(contact?.email || '');
  const waLink = chosen.length > 0 && whatsAppNumber
    ? `https://wa.me/${whatsAppNumber}?text=${encodeURIComponent(message)}`
    : null;
  const mailLink = chosen.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    ? `mailto:${email}?subject=${encodeURIComponent('Your training courses')}&body=${encodeURIComponent(message)}`
    : null;
  const toggle = id => {
    setSelectedIds(current => current.includes(id) ? current.filter(x => x !== id) : [...current, id]);
    setCopied(false);
  };
  const selectDueTraining = () => {
    setSelectedIds(renewalPlan.suggested.map(item => item.id));
    setCopied(false);
  };


  return (
    <section className="rounded-xl border border-slate-200 bg-white" data-testid="manager-training-resources">
      <button type="button" className="flex w-full min-h-12 items-center justify-between px-4 py-3 text-left text-sm font-semibold text-slate-900"
        aria-expanded={open} onClick={() => setOpen(v => !v)}>
        Training links & sharing (manager only)
        <ChevronDown className={`h-4 w-4 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="space-y-3 border-t p-4">
          <p className="text-xs text-slate-600">Only free courses with verified provider accreditation are listed. A CPD course might still need practical training or a different level. Choose only what this worker needs.</p>
          <div className="space-y-2 rounded-xl border border-teal-100 bg-teal-50 p-3"
            data-testid="manager-renewal-plan">
            <p className="text-sm font-semibold text-teal-950">Training due for this person</p>
            {renewalPlan.needs.length > 0 ? (
              <>
                <p className="text-xs text-teal-900">
                  {renewalPlan.needs.map(need => need.title).join(' · ')}
                </p>
                <button type="button" disabled={renewalPlan.suggested.length === 0}
                  onClick={selectDueTraining}
                  className="min-h-11 rounded-lg bg-teal-800 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                  data-testid="manager-select-due-training">
                  Suggest matching course links
                </button>
                <p className="text-xs text-teal-900">
                  Suggestions are not sent. Check each worker's role, course level and practical requirements before sharing.
                </p>
                {renewalPlan.unresolved.length > 0 && (
                  <p role="alert" className="text-xs text-amber-900">
                    Needs manual course matching: {renewalPlan.unresolved.map(x => x.title).join(', ')}.
                  </p>
                )}
              </>
            ) : (
              <p className="text-xs text-teal-900">No missing or expiring training actions currently identified by the compliance engine.</p>
            )}
          </div>
          <details className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
            <summary className="cursor-pointer font-semibold">Induction and renewal rules</summary>
            <p className="mt-2"><strong>{TRAINING_RENEWAL_POLICY.bridgeTitle}:</strong> {TRAINING_RENEWAL_POLICY.bridgeMessage}</p>
            <p className="mt-2"><strong>{TRAINING_RENEWAL_POLICY.careCertificateTitle}:</strong> {TRAINING_RENEWAL_POLICY.careCertificateMessage}</p>
          </details>
          <div className="max-h-80 space-y-3 overflow-y-auto pr-1" data-testid="manager-training-resource-list">
            {TOPICS.map(([key, topic]) => (
              <div key={key} className="rounded-lg border border-slate-200 p-3">
                <p className="text-sm font-semibold text-slate-800">{topic}</p>
                {resources.filter(item => item.key === key).map(item => (
                  <label className="mt-2 flex gap-3 text-sm text-slate-700" key={item.id}>
                    <input type="checkbox" className="mt-1 h-4 w-4 accent-teal-700"
                      checked={selectedIds.includes(item.id)} onChange={() => toggle(item.id)}
                      aria-label={`Select ${item.topic}: ${item.title}`} />
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium">{item.title}</span>
                      <span className="text-xs text-slate-600">
                        {item.price === 'Free' ? 'Free · CPD accredited · City & Guilds assured (theory)' : item.type === 'practical' ? 'Practical booking' : 'Provider course'}
                        {' · '}{item.provider || 'Provider'}
                      </span>
                      <a href={item.url} target="_blank" rel="noopener noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-xs text-teal-800 underline">
                        View course <ExternalLink className="h-3 w-3" />
                      </a>
                      {item.note && <span className="block text-xs text-slate-600">{item.note}</span>}
                    </span>
                  </label>
                ))}
              </div>
            ))}
          </div>
          <p className="text-xs font-medium text-slate-700">{chosen.length} course link{chosen.length === 1 ? '' : 's'} selected</p>
          <div className="flex flex-wrap gap-2">
            {waLink ? (
              <a href={waLink} target="_blank" rel="noopener noreferrer"
                data-testid="manager-send-training-whatsapp"
                className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-teal-800 px-3 py-2 text-xs font-semibold text-white">
                <MessageCircle className="h-4 w-4" /> Open WhatsApp
              </a>
            ) : (
              <span className="rounded-lg border bg-slate-50 px-3 py-3 text-xs text-slate-500">
                WhatsApp {chosen.length ? 'number unavailable' : '— choose a course'}
              </span>
            )}
            {mailLink && (
              <a href={mailLink} data-testid="manager-send-training-email"
                className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-800">
                <Mail className="h-4 w-4" /> Open email
              </a>
            )}
            <button type="button" disabled={!message}
              className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-800 disabled:opacity-50"
              onClick={async () => {
                try { await navigator.clipboard.writeText(message); setCopied(true); }
                catch (_error) { setContactError('Copy unavailable. Open WhatsApp or email instead.'); }
              }}>
              <Copy className="h-4 w-4" /> {copied ? 'Copied' : 'Copy message'}
            </button>
          </div>
          {contactError && <p role="alert" className="text-xs text-amber-800">{contactError}</p>}
          <p className="text-xs text-slate-500">WhatsApp/email opens a prepared message. The manager must press Send there. CareDock does not claim delivery or approve a training certificate.</p>
          <p className="text-xs text-amber-800">{TRAINING_RENEWAL_POLICY.feeSafeguard}</p>
        </div>
      )}
    </section>
  );
}
